import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/context/AuthContext";
import { useState } from 'react';
import { GoArrowUp } from "react-icons/go";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import TiptapEditor from '@/components/Editor/TiptapEditor';
import { Skeleton } from "@/components/ui/skeleton";

// Helper function to sanitize comment JSON so that images only persist their S3 key (and alt text)
function sanitizeCommentJson(json) {
    if (!json || !json.content || !Array.isArray(json.content)) return json;
    
    const sanitizeNode = (node) => {
        if (node.type === 's3Image' && node.attrs) {
            // Only keep the necessary attributes—instead of a signed URL that might expire.
            const newAttrs = {
                key: node.attrs.key,
                size: node.attrs.size || 'full',
                textAlign: node.attrs.textAlign || 'left'
            };
            if (node.attrs.alt) {
                newAttrs.alt = node.attrs.alt;
            }
            return { ...node, attrs: newAttrs };
        }
        if (node.content && Array.isArray(node.content)) {
            return { ...node, content: node.content.map(sanitizeNode) };
        }
        return node;
    };

    return { ...json, content: json.content.map(sanitizeNode) };
}

export default function TaskCommentEditor({ isLoading, task, onTaskChange, editorRef }) {
    if (isLoading) {
        return (
            <div>
                <div className="rounded-md border bg-muted/40">
                    <div className="relative">
                        <div className="min-h-[100px] py-3 px-3">
                        <Skeleton className="h-4 w-36 mb-2" />
                        </div>
                        <Skeleton className="absolute bottom-2 right-2 h-8 w-8" />
                    </div>
                </div>
            </div>
        );
    }

    const [comment, setComment] = useState({ text: '', json: null });
    const [isSubmittingComment, setIsSubmittingComment] = useState(false);
    const { toast } = useToast();
    const { axiosInstance } = useAuth();

    const handleCommentSubmit = async () => {
        if (!comment.text.trim()) return;

        try {
            setIsSubmittingComment(true);
            
            // Sanitize the comment JSON so that any inline image has only the key (and alt) before submitting
            const sanitizedJson = sanitizeCommentJson(comment.json);
            const response = await axiosInstance.post(
                `/tasks/${task.organization.slug}/${task.key}/comments`,
                {
                    content: comment.text,
                    json: sanitizedJson
                }
            );
            
            if (onTaskChange) {
                onTaskChange(response.data);
            }

            // Clear the editor
            editorRef.current?.commands?.setContent('');
            setComment({ text: '', json: null });
        } catch (error) {
            console.error('Error adding comment:', error);
            toast({
                description: "Failed to add comment",
                variant: "destructive"
            });
        } finally {
            setIsSubmittingComment(false);
        }
    };

    return (
        <div className="relative">
            <div className="rounded-md border bg-muted/40">
                <div className="relative">
                    <TiptapEditor
                        ref={editorRef}
                        placeholder="Add a comment..."
                        withMentions
                        withCodeBlocks
                        withBubbleMenu
                        withS3Uploads
                        className="min-h-[100px] py-3 px-3"
                        axiosInstance={axiosInstance}
                        organizationSlug={task?.organization?.slug}
                        taskKey={task?.key}
                        onEditorReady={(editor) => {
                            editorRef.current = editor;
                        }}
                        onUpdate={({ editor }) => {
                            setComment({
                                text: editor.getText(),
                                json: editor.getJSON()
                            });
                        }}
                        onKeyDown={(view, event, editor) => {
                            if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                                handleCommentSubmit();
                                return true;
                            }
                            return false;
                        }}
                    />
                    <Button
                        size="icon"
                        variant="secondary"
                        onClick={handleCommentSubmit}
                        disabled={!comment.text.trim() || isSubmittingComment || isLoading}
                        className="absolute bottom-2 right-2 h-8 w-8"
                    >
                        {!isSubmittingComment ?
                            <GoArrowUp className="h-4 w-4" />
                        :
                            <AiOutlineLoading3Quarters className="h-4 w-4 animate-spin" />
                        }
                    </Button>
                </div>
            </div>
            <p className="mt-1.5 text-[10px] text-muted-foreground flex items-center gap-1">
                Press
                <span className="inline-flex items-center border rounded px-0.5 py-[1px] text-[10px] bg-muted/30">
                    ⌘
                </span>
                <span>+</span>
                <span className="inline-flex items-center border rounded px-0.5 py-[1px] text-[10px] bg-muted/30">
                    Enter
                </span>
                to submit comment
            </p>
        </div>
    );
}